import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, MapPin, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel, EmptyInline } from "@/pages/shared";

// Meldingen en live aanwezigheid van de controleurs (Inspection-app).
export default function AdminInspection() {
  const { t } = useLang();
  const [reports, setReports] = useState([]);
  const [presence, setPresence] = useState([]);
  const [showHandled, setShowHandled] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([
        api.get(`/inspection/reports?handled=${showHandled}`),
        api.get("/inspection/presence"),
      ]);
      setReports(r.data); setPresence(p.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [showHandled]);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  const handle = async (id) => {
    try { await api.patch(`/inspection/reports/${id}`); await load(); toast.success(t("saved")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2">
        <Panel title={t("inspection_reports")} testid="inspection-reports-panel"
          action={
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowHandled((v) => !v)} data-testid="toggle-handled">
                {showHandled ? t("inspection_open") : t("inspection_handled")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => load()} data-testid="reload-reports"><RefreshCw className="h-4 w-4" /></Button>
            </div>
          }>
          <div className="p-5 space-y-3">
            {reports.length === 0 ? <EmptyInline /> : reports.map((r) => (
              <div key={r.id} className="rounded-xl border border-border p-3 space-y-1.5" data-testid={`report-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground">{r.pitch_code} · {t(`insp_cat_${r.category}`)}</p>
                  {!r.handled && (
                    <Button size="sm" variant="outline" onClick={() => handle(r.id)} data-testid={`handle-${r.id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" />{t("mark_handled")}
                    </Button>
                  )}
                </div>
                {r.note && <p className="text-xs text-muted-foreground">{r.note}</p>}
                <p className="text-[11px] text-muted-foreground">
                  {r.user_name} · {new Date(r.created_at).toLocaleString()}
                  {r.observed_status ? ` · ${t("insp_observed")}: ${r.observed_status}` : ""}
                </p>
                {r.lat ? (
                  <a className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                    href={`https://maps.google.com/?q=${r.lat},${r.lng}`} target="_blank" rel="noreferrer">
                    <MapPin className="h-3 w-3" />{r.lat.toFixed(5)}, {r.lng.toFixed(5)}
                  </a>
                ) : null}
                {r.photo_url && (
                  <a href={r.photo_url} target="_blank" rel="noreferrer" className="block text-[11px] text-primary hover:underline">
                    {t("insp_photo")}
                  </a>
                )}
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title={t("inspection_presence")} testid="inspection-presence-panel">
        <div className="p-5 space-y-2">
          {presence.length === 0 ? <EmptyInline /> : presence.map((p) => (
            <div key={p.user_id} className="rounded-lg border border-border px-3 py-2 text-xs" data-testid={`presence-${p.user_id}`}>
              <p className="font-semibold text-foreground">{p.user_name || p.user_id}</p>
              <p className="text-muted-foreground">
                {p.selected_pitch ? `${t("pitch")}: ${p.selected_pitch} · ` : ""}
                {new Date(p.at).toLocaleTimeString()}
              </p>
              <a className="text-primary hover:underline" href={`https://maps.google.com/?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer">
                {p.lat?.toFixed(5)}, {p.lng?.toFixed(5)}
              </a>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
